import { ColumnDef } from "@tanstack/react-table";
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import { Button } from "../../button";
import { ArrowUpDown, Trash } from "lucide-react";
import { t } from "i18next";
import TableWrapper from "../TableWrapper";
import DeleteBuildingModal from "../../modal/DeleteBuildingModal";
import { Building } from "../../../hooks/common/interface";
import { useTranslation } from "react-i18next";
import { TABLE_IDS } from "../../../constants/common";
interface BuildingsConfigurationTableProps {
  buildings?: Building[];
  setSelectedBuildingId: Dispatch<SetStateAction<number>>;
  page: number;
  pages: number;
  setPage: Dispatch<SetStateAction<number>>;
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  count: number;
  pageSize: number;
  setPageSize: Dispatch<SetStateAction<number>>;
}

const BuildingsConfigurationTable = ({
  buildings,
  setSelectedBuildingId,
  pages,
  page,
  setPage,
  search,
  setSearch,
  count,
  pageSize,
  setPageSize,
}: BuildingsConfigurationTableProps) => {
  const { i18n } = useTranslation();

  const [isDeleteBuildingModalOpen, setIsDeleteBuildingModalOpen] =
    useState(false);
  const [selectedDeleteBuildingId, setSelectedDeleteBuildingId] = useState(0);

  const handleDeleteBuilding = useCallback(async (buildingId: number) => {
    setIsDeleteBuildingModalOpen(true);
    setSelectedDeleteBuildingId(buildingId);
  }, []);

  const locationsColumns: ColumnDef<Building>[] = useMemo(
    () => [
      {
        accessorKey: "n_BuildingID",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("buildingId")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span
              onClick={() => setSelectedBuildingId(row.original.n_BuildingID)}
              className="text-blue-800 underline cursor-pointer capitalize"
            >
              {row.original.n_BuildingID}
            </span>
          );
        },
      },
      {
        accessorKey: "s_BuildingName_En",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("buildingNameEn")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return <span>{row.original.s_BuildingName_En}</span>;
        },
      },
      {
        accessorKey: "s_BuildingName_Ar",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("buildingNameAr")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return <span>{row.original.s_BuildingName_Ar}</span>;
        },
      },
      {
        accessorKey: "s_LocationName_En",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("location")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span>
              {i18n.language === "en"
                ? row.original.s_LocationName_En
                : row.original.s_LocationName_Ar}
            </span>
          );
        },
      },
      {
        accessorKey: "s_MapLocationLink",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("locationLink")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
      },
      {
        accessorKey: "s_Description",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("description")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return <span>{row.original.s_Description}</span>;
        },
      },
      {
        header: t("action"),
        cell: ({ row }) => {
          return (
            <Trash
              onClick={() => handleDeleteBuilding(row.original.n_BuildingID)}
              className="w-5 h-5 text-red-600 cursor-pointer"
            />
          );
        },
      },
    ],
    [handleDeleteBuilding, i18n.language, setSelectedBuildingId]
  );

  return (
    <>
      <TableWrapper
        data={buildings}
        columns={locationsColumns}
        isColumnChooser={false}
        search={search}
        setSearch={setSearch}
        page={page}
        setPage={setPage}
        totalPages={pages}
        count={count}
        isSearch
        tableId={TABLE_IDS.BUILDINGS_CONFIG}
        pageSize={pageSize}
        setPageSize={setPageSize}
      />

      <DeleteBuildingModal
        buildingId={selectedDeleteBuildingId}
        isOpen={isDeleteBuildingModalOpen}
        onOpenChange={() => setIsDeleteBuildingModalOpen(false)}
      />
    </>
  );
};

export default React.memo(BuildingsConfigurationTable);
